/**
 * Automation Canvas Block Component (Edgeless View)
 *
 * Renders the automation block on the canvas/edgeless surface.
 * Follows the Note-Reduce Pattern for correct zoom/scale positioning.
 *
 * Layout zones:
 * - Header: status dot (last/live run state), workflow title, trigger pill
 *   with client-side next-run countdown, enable toggle, settings.
 * - Pipeline: Input → Workflow → Output as a directed flow with counts.
 * - Health footer: run-history bars + KPIs from the runtime bridge.
 * - Details: collapsible column-mapping section (previous main body).
 *
 * Local-first contract: everything above renders from persisted Y.js props
 * first. Runtime data (run history, live step) is an additive overlay from
 * `window.__automationRuntimeBridge`; when the bridge is absent or offline
 * the persisted state simply stays — no spinners, no layout flicker.
 */

import { I18n } from '@affine/i18n';
import type { FilterGroup } from '@blocksuite/affine/data-view';
import { ColorScheme } from '@blocksuite/affine-model';
import {
  EDGELESS_BLOCK_CHILD_PADDING,
  EDGELESS_CANVAS_CARD_SHADOW,
} from '@blocksuite/affine-shared/consts';
import {
  SidebarExtensionIdentifier,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import {
  getTypeColor,
  normalizeTypeLabel,
  renderStreamLainDisclosure,
  renderStreamLainDisplayItemRow,
  streamLainDisclosureStyles,
} from '@blocksuite/affine-shared/utils';
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';
import {
  ArrowDownBigIcon,
  ArrowDownSmallIcon,
  ArrowUpBigIcon,
  DatabaseTableViewIcon,
  FilterIcon,
  LinkedPageIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PlayFillIcon,
  ProgressIcon,
  RedoIcon,
  TimePanelIcon,
} from '@blocksuite/icons/lit';
import {
  getGfxBlockElementModelSize,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { darkTheme, lightTheme, themeToVar } from '@streamlain/theme';
import {
  darkThemeV2,
  lightThemeV2,
  themeToVar as themeToVarV2,
} from '@streamlain/theme/v2';
import { css, html, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  AutomationBlockSchema,
  DEFAULT_AUTOMATION_HEIGHT,
  DEFAULT_AUTOMATION_WIDTH,
} from '../schemas/index.js';
import { AutomationBlockComponent } from './automation-block.js';
import * as styles from './automation-canvas-block.css.js';
import { resolveAutomationCanvasSelection } from './automation-canvas-selection.js';
import {
  hasCustomAutomationTitle,
  readAutomationTitleEditorValue,
  resolveAutomationName,
  resolveAutomationTitleWrite,
} from './automation-name.js';

// Database bridge for runtime name and column lookup
interface ColumnInfo {
  id: string;
  name: string;
  type: string;
}
interface DatabaseBridge {
  findDatabaseByBlockId?: (
    blockId: string
  ) => { databaseTitle: string; docId: string } | undefined;
  getColumnsForDatabase?: (blockId: string) => ColumnInfo[] | null;
}

// Runtime bridge for execution state (run history + live run).
// Structural mirror of AutomationRuntimeSnapshot in
// packages/frontend/core/src/modules/streamlain/services/automation-runtime-bridge.ts —
// blocksuite packages cannot import frontend/core, so the shape is
// re-declared here (same pattern as DatabaseBridge above).
interface AutomationRunSummaryLike {
  id: string;
  status: 'success' | 'error' | 'cancelled' | 'running';
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  error?: string;
  stepCount?: number;
  inputRows?: number;
  rowsWritten?: number;
}
interface AutomationActiveRunLike {
  startedAt: number;
  stepName?: string;
  stepIndex?: number;
  stepCount?: number;
  percent?: number;
}
interface AutomationRuntimeSnapshotLike {
  history: AutomationRunSummaryLike[];
  activeRun: AutomationActiveRunLike | null;
  schedule: {
    enabled: boolean;
    lastRunAt: number | null;
    nextRunAt: number | null;
  } | null;
}
interface AutomationRuntimeBridge {
  getRuntime: (blockId: string) => AutomationRuntimeSnapshotLike | null;
  ensureRuntime: (blockId: string, docId?: string) => void;
  refreshSchedule?: (blockId: string, docId: string) => void;
  releaseRuntime?: (blockId: string) => void;
}

declare const window: Window & {
  __databaseBridge?: DatabaseBridge;
  __automationRuntimeBridge?: AutomationRuntimeBridge;
};

export const AFFINE_AUTOMATION_CANVAS = 'affine-automation-canvas';
const LEGACY_AUTOMATION_WIDTH = 800;

const AUTOMATION_CLASSIC_THEME_KEYS = [
  'backgroundPrimaryColor',
  'black10',
  'textPrimaryColor',
  'textSecondaryColor',
  'warningColor',
] as const;

const AUTOMATION_V2_THEME_KEYS = [
  'button/primary',
  'button/pureWhiteText',
  'button/success',
  'icon/disable',
  'icon/primary',
  'icon/secondary',
  'icon/tertiary',
  'layer/background/error',
  'layer/background/hoverOverlay',
  'layer/background/overlayPanel',
  'layer/background/primary',
  'layer/background/secondary',
  'layer/insideBorder/border',
  'status/error',
  'status/success',
  'text/primary',
  'text/secondary',
  'text/tertiary',
  'toast/iconState/regular',
] as const;

export function resolveAutomationCanvasThemeVariables(theme: ColorScheme) {
  const classicTheme = theme === ColorScheme.Dark ? darkTheme : lightTheme;
  const v2Theme = theme === ColorScheme.Dark ? darkThemeV2 : lightThemeV2;

  return Object.fromEntries([
    ...AUTOMATION_CLASSIC_THEME_KEYS.map(key => [
      themeToVar(key),
      classicTheme[key],
    ]),
    ...AUTOMATION_V2_THEME_KEYS.map(key => [themeToVarV2(key), v2Theme[key]]),
  ]);
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSeconds = Math.ceil(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
  }
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 48) return `${totalHours}h ${totalMinutes % 60}m`;
  return `${Math.floor(totalHours / 24)}d`;
}

function formatDurationMs(ms: number): string {
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Bars rendered in the health footer (newest run is the rightmost bar). */
const RUN_BAR_COUNT = 12;
const RUN_BAR_MIN_HEIGHT = 5;
const RUN_BAR_MAX_HEIGHT = 16;
const MAX_INLINE_WORKFLOW_STEPS = 7;

interface AutomationWorkflowStepSummaryLike {
  id: string;
  name: string;
  type: string;
}

export class AutomationCanvasBlockComponent extends toGfxBlockComponent(
  AutomationBlockComponent
) {
  private _autoMeasuredTransientXYWH: SerializedXYWH | null = null;

  static override styles = css`
    ${unsafeCSS(streamLainDisclosureStyles)}

    affine-automation-canvas .workflow-steps-disclosure {
      position: relative;
      width: fit-content;
      margin: 3px 0 0;
      border: 0;
      background: transparent;
    }

    affine-automation-canvas
      .workflow-steps-disclosure
      > .streamlain-inline-disclosure-trigger {
      min-height: 20px;
      padding: 0 5px;
      color: var(--streamlain-v2-text-secondary);
      font-size: 12px;
    }

    affine-automation-canvas
      .workflow-steps-disclosure
      > .streamlain-inline-disclosure-body {
      position: absolute;
      z-index: 20;
      left: 0;
      top: calc(100% + 4px);
      width: min(260px, calc(100vw - 32px));
      border: 0.5px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-overlayPanel);
      box-shadow: var(
        --streamlain-overlay-panel-shadow,
        0 12px 32px rgba(0, 0, 0, 0.18)
      );
    }

    affine-automation-canvas .workflow-steps-disclosure-list {
      max-height: 220px;
      padding: 4px;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.borderRadius = '8px';
    this.style.boxShadow = EDGELESS_CANVAS_CARD_SHADOW;

    const selection = this.gfx.selection;

    this._selected = selection.has(this.model.id);
    this._editing = this._selected && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        this._selected = selection.has(this.model.id);
        this._editing = this._selected && selection.editing;
      })
    );

    const titleText = this.model.props.title;
    const titleUpdate = () => this.requestUpdate();
    if (titleText?.yText) {
      titleText.yText.observe(titleUpdate);
      this._disposables.add(() => {
        titleText.yText.unobserve(titleUpdate);
      });
    }

    // Re-render when any prop changes (e.g. workflowName set by sidebar)
    this._disposables.add(
      this.model.propsUpdated.subscribe(() => {
        this._rebaseAutoMeasuredTransientXYWH();
        this.requestUpdate();
      })
    );

    // Re-render when database discovery bridge becomes available or updates
    // (bridge is set asynchronously by React — without this, columns show as empty on page load)
    const onBridgeUpdate = () => this.requestUpdate();
    window.addEventListener('database-discovery-updated', onBridgeUpdate);
    this._disposables.add(() => {
      window.removeEventListener('database-discovery-updated', onBridgeUpdate);
    });

    // Attach runtime bridge (run history + live run) and re-render on updates.
    // ensureRuntime lazily fetches persisted history once the backend is
    // reachable; offline it is deferred without any visible loading state.
    window.__automationRuntimeBridge?.ensureRuntime(
      this.model.id,
      this.model.store.id
    );
    const onRuntimeReady = () => {
      window.__automationRuntimeBridge?.ensureRuntime(
        this.model.id,
        this.model.store.id
      );
      this.requestUpdate();
    };
    window.addEventListener('automation-runtime-ready', onRuntimeReady);
    const onRuntimeUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId: string }>).detail;
      if (detail?.blockId === this.model.id) {
        this.requestUpdate();
      }
    };
    window.addEventListener('automation-runtime-updated', onRuntimeUpdate);
    this._disposables.add(() => {
      window.removeEventListener('automation-runtime-ready', onRuntimeReady);
      window.removeEventListener('automation-runtime-updated', onRuntimeUpdate);
      window.__automationRuntimeBridge?.releaseRuntime?.(this.model.id);
    });

    // 1Hz ticker for the next-run countdown and the live "running for"
    // elapsed display. Only re-renders while something time-based is visible.
    const tickerId = setInterval(() => {
      if (this._needsTicker()) {
        this.requestUpdate();
      }
      const schedule = window.__automationRuntimeBridge?.getRuntime(
        this.model.id
      )?.schedule;
      if (
        schedule?.enabled &&
        schedule.nextRunAt !== null &&
        schedule.nextRunAt <= Date.now()
      ) {
        window.__automationRuntimeBridge?.refreshSchedule?.(
          this.model.id,
          this.model.store.id
        );
      }
    }, 1000);
    this._disposables.add(() => clearInterval(tickerId));

    // Re-render when sidebar writes a new title (may be hidden behind workflow viewer overlay)
    const onTitleChanged = () => this.requestUpdate();
    window.addEventListener('automation-title-changed', onTitleChanged);
    this._disposables.add(() => {
      window.removeEventListener('automation-title-changed', onTitleChanged);
    });

    // Workflow deletion handling is done at editor level (blocksuite-editor.tsx)
    // where block.model.props gives Y.js Text objects (not string proxies).
    // The Lit reactive proxy returns strings, making Y.js Text mutation impossible here.
  }

  private _emitAutoMeasuredTransientGeometryUpdated() {
    this.gfx.grid.update(this.model);
    this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
  }

  private _setAutoMeasuredTransientXYWH(nextXYWH: SerializedXYWH | null) {
    const previousXYWH = this._autoMeasuredTransientXYWH;
    this._autoMeasuredTransientXYWH = nextXYWH;

    if (!nextXYWH) {
      if (previousXYWH && this.model.transientXYWH === previousXYWH) {
        this.model.transientXYWH = undefined;
        this.refreshGfxTransform();
        this._emitAutoMeasuredTransientGeometryUpdated();
        this.requestUpdate();
      }
      return;
    }

    const currentXYWH = this.model.transientXYWH;
    if (currentXYWH === nextXYWH) return;
    if (currentXYWH && currentXYWH !== previousXYWH) return;

    this.model.transientXYWH = nextXYWH;
    // Apply the same transient bound to the block host before notifying
    // selection consumers. A Lit update alone is asynchronous, so the
    // selection overlay could otherwise move one frame ahead of the block.
    // This is presentation-only: transientXYWH never enters the Yjs history.
    this.refreshGfxTransform();
    this._emitAutoMeasuredTransientGeometryUpdated();
    this.requestUpdate();
  }

  private _rebaseAutoMeasuredTransientXYWH() {
    if (
      !this._autoMeasuredTransientXYWH ||
      this.model.transientXYWH !== this._autoMeasuredTransientXYWH
    ) {
      return;
    }

    const baseBound = Bound.deserialize(this.model.props.xywh);
    const transientBound = Bound.deserialize(this._autoMeasuredTransientXYWH);
    transientBound.x = baseBound.x;
    transientBound.y = baseBound.y;
    transientBound.w = baseBound.w;
    this._setAutoMeasuredTransientXYWH(transientBound.serialize());
  }

  override firstUpdated() {
    const container = this.renderRoot.querySelector(
      '.automation-canvas-content'
    ) as HTMLElement | null;
    if (!container) return;

    const updateHeight = () => {
      // Title-editor updates are internal state. Recomputing geometry while
      // the input is active can recreate the GFX view and steal focus.
      if (this._isEditingTitle) return;
      const { height } = getGfxBlockElementModelSize(container);
      if (!height) return;

      const contentHeight =
        Math.max(height, container.scrollHeight) +
        2 * EDGELESS_BLOCK_CHILD_PADDING;

      const bound = Bound.deserialize(this.model.props.xywh);
      const hasLegacyWidth = Math.abs(bound.w - LEGACY_AUTOMATION_WIDTH) <= 1;
      const targetHeight = Math.max(contentHeight, DEFAULT_AUTOMATION_HEIGHT);
      const heightChanged = Math.abs(bound.h - targetHeight) > 1;
      if (!hasLegacyWidth && !heightChanged) {
        this._setAutoMeasuredTransientXYWH(null);
        return;
      }

      if (hasLegacyWidth) {
        bound.x += (bound.w - DEFAULT_AUTOMATION_WIDTH) / 2;
        bound.w = DEFAULT_AUTOMATION_WIDTH;
      }
      bound.h = targetHeight;
      // Measurement is presentation state. Never write Y.Doc geometry merely
      // because a block loaded, a runtime chip changed, or CSS reflowed.
      this._setAutoMeasuredTransientXYWH(bound.serialize());
    };

    requestAnimationFrame(updateHeight);

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    observer.observe(container, { childList: true, subtree: true });
    this._disposables.add(() => {
      observer.disconnect();
      this._setAutoMeasuredTransientXYWH(null);
    });
  }

  override getRenderingRect() {
    const bound = Bound.deserialize(
      this.model.transientXYWH ?? this.model.props.xywh
    );

    return {
      x: bound.x,
      y: bound.y,
      w: bound.w,
      h: bound.h,
      zIndex: this.toZIndex(),
    };
  }

  override renderGfxBlock() {
    const edgelessTheme = this.std.get(ThemeProvider).theme$.value;
    const style = {
      ...resolveAutomationCanvasThemeVariables(edgelessTheme),
      borderRadius: '8px',
    };

    return html`
      <div
        class=${styles.automationContainer}
        style=${styleMap(style)}
        data-editing=${this._editing}
      >
        <automation-canvas-background
          .editing=${this._editing}
          .automation=${this.model}
        ></automation-canvas-background>
        <div
          class=${styles.clipContainer}
          style=${styleMap({ overflow: 'clip' })}
        >
          <div class="automation-canvas-content ${styles.canvasContent}">
            ${this.renderPageContent()}
          </div>
        </div>
        <automation-canvas-mask
          .model=${this.model}
          .host=${this.host}
          .zoom=${this.gfx.viewport.zoom ?? 1}
          .disableMask=${this._selected}
          .editing=${this._editing}
          @pointerdown=${this.activateCanvasSelectionFromMask}
        ></automation-canvas-mask>
      </div>
    `;
  }

  private readonly activateCanvasSelectionFromMask = (event: PointerEvent) => {
    if (event.button !== 0 || !event.isPrimary || this.model.isLocked()) {
      return;
    }
    event.stopPropagation();
    this.gfx.selection.set({
      elements: resolveAutomationCanvasSelection({
        additive: event.shiftKey || event.ctrlKey || event.metaKey,
        selectedIds: this.gfx.selection.selectedIds,
        targetId: this.model.id,
      }),
      editing: false,
    });
  };

  override renderPageContent() {
    const { model } = this;
    const workflowId = model.props.workflowId;
    const hasWorkflow = !!workflowId;
    const hasPipelineConfig =
      hasWorkflow ||
      !!model.props.sourceTableId ||
      model.props.triggerConfig?.type === 'webhook' ||
      (model.props.outputTables?.length ?? 0) > 0;

    const runtime =
      window.__automationRuntimeBridge?.getRuntime(model.id) ?? null;
    const activeRun = runtime?.activeRun ?? null;
    const history = runtime?.history ?? [];

    return html`
      <div class=${styles.content}>
        ${this._renderHeader(hasWorkflow, activeRun)}
        ${hasPipelineConfig
          ? html`
              ${this._renderPipeline(activeRun, history)}
              ${this._detailsExpanded ? this._renderDetails() : nothing}
              ${hasWorkflow ? this._renderFooter(activeRun, history) : nothing}
            `
          : this._renderEmptyState()}
      </div>
    `;
  }

  @state()
  private accessor _selected = false;

  @state()
  private accessor _editing = false;

  @state()
  private accessor _detailsExpanded = false;

  @state()
  private accessor _isEditingTitle = false;

  @state()
  private accessor _titleDraft = '';

  private _needsTicker(): boolean {
    const triggerConfig = this.model.props.triggerConfig;
    const schedule = window.__automationRuntimeBridge?.getRuntime(
      this.model.id
    )?.schedule;
    if (
      triggerConfig?.type === 'scheduled' &&
      (schedule?.enabled ?? triggerConfig.enabled)
    ) {
      return true;
    }
    return !!window.__automationRuntimeBridge?.getRuntime(this.model.id)
      ?.activeRun;
  }

  // ===========================================================================
  // Header
  // ===========================================================================

  private _statusDotState(
    activeRun: AutomationActiveRunLike | null
  ): 'running' | 'success' | 'error' | 'cancelled' | 'idle' {
    if (activeRun) return 'running';
    switch (this.model.props.lastRunStatus) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'cancelled':
        return 'cancelled';
      case 'running':
        // Persisted 'running' without a live run means the state is stale
        // (e.g. reload during a run) — show idle rather than a fake pulse.
        return 'idle';
      default:
        return 'idle';
    }
  }

  private _renderHeader(
    hasWorkflow: boolean,
    activeRun: AutomationActiveRunLike | null
  ) {
    const model = this.model;
    const workflowName = (model.props as Record<string, unknown>)
      .workflowName as string | null;
    const automationIdentity = {
      title: model.props.title,
      titleMode: model.props.titleMode,
      workflowName,
    };
    const displayTitle = resolveAutomationName(
      automationIdentity,
      I18n.t('com.streamlain.streamlain.common.new-automation')
    );
    const hasMeaningfulTitle =
      hasWorkflow || hasCustomAutomationTitle(automationIdentity);

    const dotState = this._statusDotState(activeRun);
    const dotTitle =
      dotState === 'running'
        ? I18n.t('com.streamlain.streamlain.process.canvas.run-active')
        : dotState === 'error'
          ? I18n.t('com.streamlain.streamlain.process.canvas.run-failed')
          : dotState === 'cancelled'
            ? I18n.t('com.streamlain.streamlain.process.canvas.run-cancelled')
            : this._formatLastRun(model.props.lastRunAt);

    return html`
      <div class=${styles.headerRow} data-role="automation-header">
        <div class=${styles.headerTopRow} data-role="automation-header-top">
          <span class=${styles.statusDotShell}>
            <span
              class=${styles.statusDot}
              data-status=${dotState}
              title=${dotTitle}
            ></span>
          </span>

          <div class=${styles.headerIdentity}>
            ${this._isEditingTitle
              ? html`<input
                  class=${classMap({
                    [styles.titleInput]: true,
                    [styles.titleTextEmpty]: !hasMeaningfulTitle,
                  })}
                  data-automation-title-editor
                  .value=${this._titleDraft}
                  title=${displayTitle}
                  aria-label=${displayTitle}
                  @input=${this._handleTitleInput}
                  @keydown=${this._handleTitleKeyDown}
                  @blur=${this._handleTitleBlur}
                  @pointerdown=${this._stopTitleEditorPointer}
                />`
              : html`<div
                  class=${classMap({
                    [styles.titleText]: true,
                    [styles.titleTextEmpty]: !hasMeaningfulTitle,
                  })}
                  data-automation-title-editor
                  title=${displayTitle}
                  @pointerdown=${this._startTitleEditing}
                >
                  ${displayTitle}
                </div>`}
          </div>
        </div>
        <div
          class=${styles.headerControlColumn}
          data-role="automation-header-controls"
        >
          <div
            class=${styles.headerActions}
            data-role="automation-header-actions"
          >
            <button
              class=${styles.settingsButton}
              @click=${this._handleOpenConfig}
              title=${I18n.t('com.streamlain.streamlain.process.open-settings')}
            >
              ${MoreHorizontalIcon()}
            </button>
            ${hasWorkflow ? this._renderToggle() : nothing}
          </div>
          ${hasWorkflow
            ? html`<div
                class=${styles.headerControlsRow}
                data-role="automation-header-trigger"
              >
                ${this._renderTriggerPill()}
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }

  private readonly _startTitleEditing = (event: PointerEvent) => {
    if (this.model.store.readonly) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.model.isLocked()) return;

    this.gfx.selection.set({
      elements: [this.model.id],
      editing: true,
    });
    this._isEditingTitle = true;
    this._titleDraft = resolveAutomationName(
      {
        title: this.model.props.title,
        titleMode: this.model.props.titleMode,
        workflowName: this.model.props.workflowName,
      },
      I18n.t('com.streamlain.streamlain.common.new-automation')
    );
    this.updateComplete
      .then(() => {
        const input = this.renderRoot.querySelector<HTMLInputElement>(
          `.${styles.titleInput}`
        );
        input?.focus();
        input?.select();
      })
      .catch(console.error);
  };

  private readonly _stopTitleEditorPointer = (event: PointerEvent) => {
    event.stopPropagation();
  };

  private readonly _handleTitleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this._titleDraft = readAutomationTitleEditorValue(input, this._titleDraft);
  };

  private readonly _handleTitleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this._commitTitle();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this._isEditingTitle = false;
      this._titleDraft = resolveAutomationName(
        {
          title: this.model.props.title,
          titleMode: this.model.props.titleMode,
          workflowName: this.model.props.workflowName,
        },
        I18n.t('com.streamlain.streamlain.common.new-automation')
      );
      return;
    }
    event.stopPropagation();
  };

  private readonly _handleTitleBlur = () => {
    this._commitTitle();
  };

  private readonly _commitTitle = () => {
    if (!this._isEditingTitle) return;

    const input = this.renderRoot.querySelector<HTMLInputElement>(
      `.${styles.titleInput}`
    );
    this._isEditingTitle = false;
    const titleDraft = readAutomationTitleEditorValue(
      input,
      this._titleDraft
    ).trim();

    const next = resolveAutomationTitleWrite(
      {
        title: this.model.props.title,
        titleMode: this.model.props.titleMode,
        workflowName: this.model.props.workflowName,
      },
      titleDraft,
      'user'
    );
    if (!next) return;

    this.model.store.captureSync();
    this.model.store.transact(() => {
      this.model.props.titleMode = next.titleMode;
      const title = this.model.props.title;
      title.replace(0, title.length, next.title);
    });
    window.dispatchEvent(new CustomEvent('automation-title-changed'));
  };

  private _renderTriggerPill() {
    const triggerConfig = this.model.props.triggerConfig;
    const schedule = window.__automationRuntimeBridge?.getRuntime(
      this.model.id
    )?.schedule;
    const triggerDisplay = this._formatTrigger(triggerConfig);
    const type = triggerConfig?.type ?? 'manual';
    const enabled = schedule?.enabled ?? triggerConfig?.enabled ?? false;

    let variant = 'default';
    if (type === 'webhook') variant = 'webhook';
    else if (type === 'scheduled' && enabled) variant = 'scheduled-active';

    let suffix: TemplateResult | typeof nothing = nothing;
    if (type === 'scheduled') {
      if (enabled) {
        const nextRun = schedule?.nextRunAt ?? null;
        if (nextRun !== null) {
          suffix = html`&nbsp;·&nbsp;<span class=${styles.pillStrong}
              >${I18n.t('com.streamlain.streamlain.process.canvas.next-run', {
                time: formatCountdown(nextRun - Date.now()),
              })}</span
            >`;
        }
      } else {
        suffix = html`&nbsp;·&nbsp;${I18n.t(
          'com.streamlain.streamlain.process.canvas.paused'
        )}`;
      }
    }

    return html`
      <button
        class=${styles.triggerPill}
        data-variant=${variant}
        @click=${this._handleOpenConfig}
        title=${I18n.t('com.streamlain.streamlain.process.configure-trigger')}
      >
        <span class=${styles.pillIcon}>${TimePanelIcon()}</span>
        ${triggerDisplay}${suffix}
      </button>
    `;
  }

  private _renderToggle() {
    const triggerConfig = this.model.props.triggerConfig;
    const isEnabled =
      window.__automationRuntimeBridge?.getRuntime(this.model.id)?.schedule
        ?.enabled ??
      triggerConfig?.enabled ??
      false;
    // sl-1ahs.4.1: Toggle only active for scheduled triggers
    const toggleAllowed = triggerConfig?.type === 'scheduled';

    return html`
      <button
        class=${styles.enableToggle}
        data-enabled=${isEnabled}
        data-disabled=${!toggleAllowed}
        ?disabled=${!toggleAllowed}
        @click=${this._handleToggleEnabled}
        title=${!toggleAllowed
          ? I18n.t('com.streamlain.streamlain.process.toggle-only-scheduled')
          : isEnabled
            ? I18n.t('com.streamlain.streamlain.process.click-to-disable')
            : I18n.t('com.streamlain.streamlain.process.click-to-enable')}
      >
        <span class=${styles.toggleKnob}></span>
      </button>
    `;
  }

  // ===========================================================================
  // Pipeline — Input → Workflow → Output
  // ===========================================================================

  private _renderPipeline(
    activeRun: AutomationActiveRunLike | null,
    history: AutomationRunSummaryLike[]
  ) {
    const connector = html`
      <div class=${styles.connector} data-role="automation-internal-edge">
        <svg
          class=${styles.connectorIcon}
          viewBox="0 0 20 38"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            class=${styles.connectorHandle}
            data-marker="source-handle"
            cx="10"
            cy="0"
            r="6"
          ></circle>
          <path
            class=${styles.connectorShaft}
            data-marker="shaft"
            d="M10 0V27"
          ></path>
          <path
            class=${styles.connectorArrowhead}
            data-marker="target-arrow"
            d="M2 26L10 38L18 26Z"
          ></path>
        </svg>
      </div>
    `;

    return html`
      <div class=${styles.pipeline}>
        ${this._renderInputStage(history)} ${connector}
        ${this._renderWorkflowStage(activeRun, history)} ${connector}
        ${this._renderOutputStage()}
      </div>
    `;
  }

  private _renderInputStage(history: AutomationRunSummaryLike[]) {
    const { sourceTableId, sourceTableName, triggerConfig } = this.model.props;
    const isWebhookTrigger = triggerConfig?.type === 'webhook';
    const bridge = window.__databaseBridge;
    const currentDocId = this.model.store.id;

    const sourceDbInfo =
      sourceTableId && !isWebhookTrigger
        ? bridge?.findDatabaseByBlockId?.(sourceTableId)
        : undefined;
    const resolvedSourceName =
      sourceDbInfo?.databaseTitle ?? sourceTableName ?? null;
    const isSourceCrossDoc = sourceDbInfo
      ? sourceDbInfo.docId !== currentDocId
      : false;

    const header = html`
      <div
        class=${styles.stageHeader}
        data-role="stage-header"
        data-stage="input"
      >
        ${I18n.t('com.streamlain.streamlain.process.tab-input')}
        ${sourceTableId && history[0]?.inputRows
          ? html`<span class=${styles.stageCount}
              >${I18n.t(
                'com.streamlain.streamlain.process.canvas.last-input-rows',
                { count: history[0].inputRows }
              )}</span
            >`
          : nothing}
      </div>
    `;

    if (!sourceTableId && !isWebhookTrigger) {
      return html`
        <div class=${styles.stage} data-role="input">
          ${header}
          <button
            class="${styles.emptyHint} ${styles.stageAction}"
            @click=${this._handleOpenConfig}
            @pointerdown=${(e: Event) => e.stopPropagation()}
          >
            ${I18n.t('com.streamlain.streamlain.process.not-configured')}
          </button>
        </div>
      `;
    }

    const name = isWebhookTrigger
      ? I18n.t('com.streamlain.streamlain.process.canvas.webhook-input')
      : resolvedSourceName || 'Source Table';

    const filterCount = this.model.filterCount;
    const secondaryCount = (this.model.props.inputTables ?? []).filter(
      table => table.role === 'secondary'
    ).length;
    const mappedCount =
      (this.model.props.inputMappings?.length ?? 0) ||
      (this.model.props.selectedColumns?.length ?? 0);
    const sourceColumns =
      sourceTableId && !isWebhookTrigger
        ? (bridge?.getColumnsForDatabase?.(sourceTableId) ?? [])
        : [];
    const mappedColumns = (this.model.props.inputMappings ?? [])
      .map(mapping => {
        const column = sourceColumns.find(item => item.id === mapping.columnId);
        return column ? { name: column.name, type: column.type } : null;
      })
      .filter((item): item is { name: string; type: string } => item !== null);

    return html`
      <div class=${styles.stage} data-role="input">
        ${header}
        <div class=${styles.stageMain}>
          <span class=${styles.stageMainIcon}>${DatabaseTableViewIcon()}</span>
          ${name}
          ${isSourceCrossDoc
            ? html`<span class=${styles.stageMainIcon}
                >${LinkedPageIcon({ width: '10', height: '10' })}</span
              >`
            : nothing}
        </div>
        ${this._renderCompactMappings(mappedColumns)}
        <div class=${styles.stageSub}>
          ${filterCount > 0
            ? html`<span class=${styles.stageChip}>
                <span class=${styles.stageChipIcon}
                  >${FilterIcon({ width: '9', height: '9' })}</span
                >
                ${I18n.t('com.streamlain.streamlain.process.canvas.filters', {
                  count: filterCount,
                })}
              </span>`
            : nothing}
          ${secondaryCount > 0
            ? html`<span class=${styles.stageChip}
                >${I18n.t('com.streamlain.streamlain.process.canvas.lookups', {
                  count: secondaryCount,
                })}</span
              >`
            : nothing}
          ${mappedCount > 0 && mappedColumns.length === 0
            ? html`<span
                >${I18n.t(
                  'com.streamlain.streamlain.process.canvas.columns-mapped',
                  { count: mappedCount }
                )}</span
              >`
            : nothing}
        </div>
      </div>
    `;
  }

  private _renderWorkflowStage(
    activeRun: AutomationActiveRunLike | null,
    history: AutomationRunSummaryLike[]
  ) {
    const model = this.model;
    const hasWorkflow = !!model.props.workflowId;
    const configuredWorkflowName =
      ((model.props as Record<string, unknown>).workflowName as
        | string
        | null) ?? model.props.title?.toString();
    const workflowName = hasWorkflow
      ? configuredWorkflowName
      : I18n.t('com.streamlain.streamlain.process.not-configured');
    const workflowSteps = Array.isArray(model.props.workflowSteps)
      ? (model.props.workflowSteps as AutomationWorkflowStepSummaryLike[])
      : [];
    const hasError =
      hasWorkflow && !activeRun && model.props.lastRunStatus === 'error';

    let sub: TemplateResult;
    if (activeRun) {
      const elapsed = formatCountdown(Date.now() - activeRun.startedAt);
      const stepInfo =
        activeRun.stepIndex !== undefined && activeRun.stepCount
          ? I18n.t('com.streamlain.streamlain.process.canvas.step-progress', {
              index: activeRun.stepIndex,
              count: activeRun.stepCount,
            })
          : activeRun.stepName;
      sub = html`
        <div class=${styles.stageSub}>
          ${stepInfo ? html`<span>${stepInfo}</span> ·` : nothing}
          <span
            >${I18n.t(
              'com.streamlain.streamlain.process.canvas.running-since',
              { time: elapsed }
            )}</span
          >
        </div>
      `;
    } else if (!hasWorkflow) {
      sub = html``;
    } else {
      const lastWithSteps = history.find(run => run.stepCount);
      const batchConfig = model.props.batchConfig;
      const chips: TemplateResult[] = [];
      if (lastWithSteps?.stepCount) {
        chips.push(
          html`<span class=${styles.stageChip}
            >${I18n.t('com.streamlain.streamlain.process.canvas.steps', {
              count: lastWithSteps.stepCount,
            })}</span
          >`
        );
      }
      if ((batchConfig?.batchSize ?? 1) > 1) {
        chips.push(
          html`<span class=${styles.stageChip}
            >${I18n.t('com.streamlain.streamlain.process.canvas.batch', {
              size: batchConfig.batchSize,
            })}</span
          >`
        );
      }
      if (batchConfig?.retry?.enabled) {
        chips.push(
          html`<span class=${styles.stageChip}
            >${I18n.t('com.streamlain.streamlain.process.canvas.retry-times', {
              count: batchConfig.retry.maxAttempts,
            })}</span
          >`
        );
      }
      sub =
        chips.length > 0
          ? html`<div class=${styles.stageSub}>${chips}</div>`
          : html``;
    }

    const runningChip = activeRun?.percent
      ? html`<span class=${styles.stageCount}
          >${Math.round(activeRun.percent)}%</span
        >`
      : nothing;

    return html`
      <div
        class=${styles.stage}
        data-role="workflow"
        data-state=${hasError ? 'error' : 'ok'}
      >
        <div
          class=${styles.stageHeader}
          data-role="stage-header"
          data-stage="process"
        >
          ${I18n.t('com.streamlain.streamlain.process.tab-process')}
          ${runningChip}
        </div>
        <div class=${styles.stageMain}>
          ${hasWorkflow
            ? html`<span class=${styles.stageMainIcon}>${PlayFillIcon()}</span>`
            : nothing}
          <span
            class=${classMap({
              [styles.stageActionLabel]: true,
              [styles.emptyHint]: !hasWorkflow,
            })}
            >${workflowName}</span
          >
        </div>
        ${hasWorkflow ? this._renderWorkflowSteps(workflowSteps) : nothing}
        ${sub}
      </div>
    `;
  }

  private _renderWorkflowSteps(steps: AutomationWorkflowStepSummaryLike[]) {
    if (steps.length === 0) return nothing;

    const inlineSteps = steps.slice(0, MAX_INLINE_WORKFLOW_STEPS);
    const stepList = html`<ol
      class=${styles.workflowStepList}
      data-role="workflow-step-list"
    >
      ${inlineSteps.map(
        step =>
          html`<li
            class=${styles.workflowStepItem}
            data-role="workflow-step"
            title=${step.name}
          >
            ${step.name}
          </li>`
      )}
    </ol>`;

    if (steps.length <= MAX_INLINE_WORKFLOW_STEPS) return stepList;

    return html`
      ${stepList}
      <div @pointerdown=${(event: Event) => event.stopPropagation()}>
        ${renderStreamLainDisclosure({
          className: 'workflow-steps-disclosure',
          summary: html`<span aria-label="All workflow steps">…</span>`,
          content: html`<div
            class="streamlain-display-list workflow-steps-disclosure-list"
          >
            ${steps.map((step, index) =>
              renderStreamLainDisplayItemRow({
                icon: html`<span>${index + 1}</span>`,
                title: step.name,
                subtitle: step.type || nothing,
                titleAttr: step.name,
              })
            )}
          </div>`,
        })}
      </div>
    `;
  }

  private _renderOutputStage() {
    const { outputTables, outputMappings, matchConfigs } = this.model.props;
    const bridge = window.__databaseBridge;
    const currentDocId = this.model.store.id;

    const header = html`
      <div
        class=${styles.stageHeader}
        data-role="stage-header"
        data-stage="output"
      >
        ${I18n.t('com.streamlain.streamlain.process.tab-output')}
        ${(outputTables?.length ?? 0) > 1
          ? html`<span class=${styles.stageCount}
              >${I18n.t('com.streamlain.streamlain.process.canvas.tables', {
                count: outputTables.length,
              })}</span
            >`
          : nothing}
      </div>
    `;

    if (!outputTables?.length) {
      return html`
        <div class=${styles.stage} data-role="output">
          ${header}
          <button
            class="${styles.emptyHint} ${styles.stageAction}"
            @click=${this._handleOpenConfig}
            @pointerdown=${(e: Event) => e.stopPropagation()}
          >
            ${I18n.t('com.streamlain.streamlain.process.not-configured')}
          </button>
        </div>
      `;
    }

    const firstTable = outputTables[0];
    const dbInfo = bridge?.findDatabaseByBlockId?.(firstTable.tableId);
    const name = dbInfo?.databaseTitle ?? firstTable.tableName;
    const isCrossDoc = dbInfo ? dbInfo.docId !== currentDocId : false;
    const extraTables = outputTables.length - 1;

    // Resolve the first match-key column name for the primary output table
    const matchConfig = firstTable.matchConfigId
      ? (matchConfigs ?? []).find(mc => mc.id === firstTable.matchConfigId)
      : undefined;
    const firstKey = matchConfig?.keys[0];
    const keyColumnName = firstKey
      ? (bridge
          ?.getColumnsForDatabase?.(firstTable.tableId)
          ?.find(column => column.id === firstKey.columnId)?.name ??
        firstKey.columnId)
      : null;

    const mappingCount = outputMappings?.length ?? 0;
    const targetColumns =
      bridge?.getColumnsForDatabase?.(firstTable.tableId) ?? [];
    const mappedColumns = (outputMappings ?? [])
      .filter(mapping => mapping.tableId === firstTable.tableId)
      .map(mapping => {
        const column = targetColumns.find(
          item => item.id === mapping.targetColumnId
        );
        return column ? { name: column.name, type: column.type } : null;
      })
      .filter((item): item is { name: string; type: string } => item !== null);

    return html`
      <div class=${styles.stage} data-role="output">
        ${header}
        <div class=${styles.stageMain}>
          <span class=${styles.stageMainIcon}>${DatabaseTableViewIcon()}</span>
          ${name}${extraTables > 0 ? ` +${extraTables}` : ''}
          ${isCrossDoc
            ? html`<span class=${styles.stageMainIcon}
                >${LinkedPageIcon({ width: '10', height: '10' })}</span
              >`
            : nothing}
        </div>
        ${this._renderCompactMappings(mappedColumns)}
        <div class=${styles.stageSub}>
          ${keyColumnName
            ? html`<span class=${styles.stageChip}>
                <span class=${styles.stageChipIcon}
                  >${LinkIcon({ width: '9', height: '9' })}</span
                >
                ${I18n.t('com.streamlain.streamlain.process.canvas.match-key', {
                  name: keyColumnName,
                })}
              </span>`
            : nothing}
          ${mappingCount > 0 && mappedColumns.length === 0
            ? html`<span
                >${I18n.t('com.streamlain.streamlain.process.canvas.mappings', {
                  count: mappingCount,
                })}</span
              >`
            : nothing}
        </div>
      </div>
    `;
  }

  private _renderCompactMappings(
    columns: Array<{ name: string; type: string }>
  ) {
    if (columns.length === 0) return nothing;
    return html`
      <ul class=${styles.compactMappingList} data-role="mapping-list">
        ${columns.slice(0, 3).map(
          column =>
            html`<li
              class=${styles.compactMappingItem}
              data-role="mapping-row"
              data-streamlain-mapping-row
            >
              <span class=${styles.compactMappingName} title=${column.name}
                >${column.name}</span
              >
              <span
                class=${styles.typeChip}
                data-streamlain-type-chip
                style=${styleMap({
                  backgroundColor: getTypeColor(column.type),
                })}
                >${normalizeTypeLabel(column.type)}</span
              >
            </li>`
        )}
        ${columns.length > 3
          ? html`<li class=${styles.compactMappingMore}>
              +${columns.length - 3}
            </li>`
          : nothing}
      </ul>
    `;
  }

  // ===========================================================================
  // Health footer — run bars + KPIs
  // ===========================================================================

  private _renderFooter(
    activeRun: AutomationActiveRunLike | null,
    history: AutomationRunSummaryLike[]
  ) {
    const terminalRuns = history.filter(run => run.status !== 'running');

    const bars = this._renderRunBars(activeRun, terminalRuns);
    const kpis: TemplateResult[] = [];

    if (terminalRuns.length > 0) {
      const successCount = terminalRuns.filter(
        run => run.status === 'success'
      ).length;
      const successPercent = Math.round(
        (successCount / terminalRuns.length) * 100
      );
      kpis.push(html`
        <span class=${styles.kpi}>
          <span
            class=${successPercent < 100 && successPercent <= 50
              ? styles.kpiError
              : styles.kpiStrong}
            >${I18n.t('com.streamlain.streamlain.process.canvas.success-rate', {
              percent: successPercent,
            })}</span
          >
        </span>
      `);

      const durations = terminalRuns
        .map(run => run.durationMs)
        .filter((value): value is number => value !== undefined && value > 0);
      if (durations.length > 0) {
        const avg =
          durations.reduce((sum, value) => sum + value, 0) / durations.length;
        kpis.push(html`
          <span class=${styles.kpi}>
            ${I18n.t('com.streamlain.streamlain.process.canvas.avg-duration', {
              duration: formatDurationMs(avg),
            })}
          </span>
        `);
      }
    }

    if (activeRun) {
      kpis.push(html`
        <span class="${styles.kpi} ${styles.kpiRunning}">
          ${I18n.t('com.streamlain.streamlain.process.canvas.run-active')}
        </span>
      `);
    } else {
      const lastRun = this.model.props.lastRunAt;
      const lastWritten = history[0]?.rowsWritten;
      kpis.push(html`
        <span
          class=${styles.kpi}
          title=${lastRun
            ? new Date(lastRun).toLocaleString()
            : I18n.t('com.streamlain.streamlain.process.last-run.never')}
        >
          <span class=${lastRun ? styles.kpiStrong : styles.kpiMuted}
            >${this._formatLastRun(lastRun)}</span
          >
          ${lastWritten !== undefined && lastWritten > 0
            ? html` ·
              ${I18n.t(
                'com.streamlain.streamlain.process.canvas.rows-written',
                { count: lastWritten }
              )}`
            : nothing}
        </span>
      `);
    }

    const statusTracking = this.model.props.statusTracking;
    const isFailed = !activeRun && this.model.props.lastRunStatus === 'error';
    const failedRun = isFailed
      ? history.find(run => run.status === 'error')
      : undefined;
    const failedText = failedRun?.error
      ? `${I18n.t(
          'com.streamlain.streamlain.process.canvas.run-failed'
        )} — ${failedRun.error}`
      : I18n.t('com.streamlain.streamlain.process.canvas.run-failed');

    return html`
      <div class=${styles.footerRow} data-role="automation-footer">
        <span
          class=${styles.footerActivityRow}
          data-role="automation-footer-activity"
        >
          <span class=${styles.footerRunSummary}>
            ${bars}
            <span class=${styles.lastRunLabel}>
              <span class=${styles.lastRunIcon}
                >${TimePanelIcon({ width: '12', height: '12' })}</span
              >
              ${I18n.t('com.streamlain.streamlain.process.last-run')}:
            </span>
            ${kpis}
          </span>
          <span class=${styles.statusActionSlot}>
            ${isFailed
              ? html`<button
                  class=${styles.retryIconButton}
                  aria-label=${I18n.t(
                    'com.streamlain.streamlain.process.canvas.retry-run'
                  )}
                  title=${failedText}
                  @click=${this._handleRetryRun}
                  @pointerdown=${(e: Event) => e.stopPropagation()}
                >
                  ${RedoIcon({ width: '12', height: '12' })}
                </button>`
              : nothing}
          </span>
        </span>
        <span class=${styles.footerRight} data-role="automation-footer-actions">
          <span
            class=${styles.trackingChip}
            data-active=${statusTracking?.enabled ?? false}
            title=${statusTracking?.enabled
              ? I18n.t(
                  'com.streamlain.streamlain.process.status-tracking-enabled'
                )
              : I18n.t(
                  'com.streamlain.streamlain.process.status-tracking-disabled'
                )}
          >
            <span class=${styles.trackingChipIcon}>${ProgressIcon()}</span>
            ${I18n.t('com.streamlain.streamlain.process.canvas.tracking')}
          </span>
          <span class=${styles.footerDetailsGroup}>
            <button
              class=${styles.detailsToggle}
              data-testid="automation-canvas-details-toggle"
              @click=${this._handleToggleDetails}
              @pointerdown=${(e: Event) => e.stopPropagation()}
            >
              ${I18n.t('com.streamlain.streamlain.process.canvas.details')}
              <span
                class=${styles.detailsToggleIcon}
                data-expanded=${this._detailsExpanded}
                >${ArrowDownSmallIcon({ width: '12', height: '12' })}</span
              >
            </button>
          </span>
        </span>
      </div>
    `;
  }

  private _renderRunBars(
    activeRun: AutomationActiveRunLike | null,
    terminalRuns: AutomationRunSummaryLike[]
  ) {
    if (terminalRuns.length === 0 && !activeRun) return nothing;

    // Oldest → newest, newest bar on the right; live run appended last.
    const runs = terminalRuns.slice(0, RUN_BAR_COUNT).reverse();

    const durations = runs
      .map(run => run.durationMs ?? 0)
      .filter(value => value > 0);
    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
    const heightFor = (durationMs: number | undefined): number => {
      if (!durationMs || maxDuration === 0) return RUN_BAR_MIN_HEIGHT + 4;
      return (
        RUN_BAR_MIN_HEIGHT +
        (durationMs / maxDuration) * (RUN_BAR_MAX_HEIGHT - RUN_BAR_MIN_HEIGHT)
      );
    };

    return html`
      <span class=${styles.runBars}>
        ${runs.map(
          run => html`
            <i
              class=${styles.runBar}
              data-status=${run.status}
              style=${styleMap({ height: `${heightFor(run.durationMs)}px` })}
              title="${new Date(run.startedAt).toLocaleString()}${run.durationMs
                ? ` · ${formatDurationMs(run.durationMs)}`
                : ''}${run.error ? ` · ${run.error}` : ''}"
            ></i>
          `
        )}
        ${activeRun
          ? html`<i
              class=${styles.runBar}
              data-status="running"
              style=${styleMap({ height: `${RUN_BAR_MAX_HEIGHT}px` })}
            ></i>`
          : nothing}
      </span>
    `;
  }

  // ===========================================================================
  // Empty state — no workflow selected
  // ===========================================================================

  private _renderEmptyState() {
    return html`
      <div class=${styles.emptyWrap}>
        ${this._renderPipeline(null, [])}
        <p class=${styles.emptyText}>
          ${I18n.t('com.streamlain.streamlain.process.canvas.empty-hint')}
        </p>
        <button
          class=${styles.emptyCta}
          @click=${this._handleOpenConfig}
          @pointerdown=${(e: Event) => e.stopPropagation()}
        >
          ${I18n.t('com.streamlain.streamlain.process.canvas.empty-cta')}
        </button>
      </div>
    `;
  }

  // ===========================================================================
  // Details — collapsible mapping section (Input / Output columns)
  // ===========================================================================

  private _renderDetails() {
    const {
      sourceTableId,
      sourceTableName,
      outputTables,
      outputMappings,
      triggerConfig,
    } = this.model.props;

    const currentDocId = this.model.store.id;
    const bridge = window.__databaseBridge;

    const isWebhookTrigger = triggerConfig?.type === 'webhook';
    const sourceDbInfo =
      sourceTableId && !isWebhookTrigger
        ? bridge?.findDatabaseByBlockId?.(sourceTableId)
        : undefined;
    const resolvedSourceName =
      sourceDbInfo?.databaseTitle ?? sourceTableName ?? null;
    const isSourceCrossDoc = sourceDbInfo
      ? sourceDbInfo.docId !== currentDocId
      : false;

    const resolvedOutputTables = (outputTables ?? []).map(table => {
      const dbInfo = bridge?.findDatabaseByBlockId?.(table.tableId);
      return {
        ...table,
        resolvedName: dbInfo?.databaseTitle ?? table.tableName,
        isCrossDoc: dbInfo ? dbInfo.docId !== currentDocId : false,
      };
    });

    return html`
      <div class=${styles.detailsSection}>
        <div class=${styles.body}>
          <div class=${styles.column}>
            <div class=${styles.columnHeader}>
              <span class=${styles.columnIcon}>${ArrowDownBigIcon()}</span>
              ${I18n.t('com.streamlain.streamlain.process.tab-input')}
            </div>
            ${this._renderInputSection(
              sourceTableId,
              resolvedSourceName,
              isSourceCrossDoc,
              isWebhookTrigger
            )}
          </div>

          <div class=${styles.column}>
            <div class=${styles.columnHeader}>
              <span class=${styles.columnIcon}>${ArrowUpBigIcon()}</span>
              ${I18n.t('com.streamlain.streamlain.process.tab-output')}
            </div>
            ${this._renderOutputSection(resolvedOutputTables, outputMappings)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Unified table section renderer for both Input and Output
   */
  private _renderTableSection(config: {
    tableName: string;
    isCrossDoc: boolean;
    columns: Array<{
      id: string;
      name: string;
      type: string;
      mappingLeft?: string;
      // sl-g3on.4.1: optional debug tooltip — falls back to mappingLeft.
      // Used by the output renderer to expose the raw field ID on hover
      // when the visible label is the workflow output field name.
      mappingLeftTooltip?: string;
      mappingLeftType?: string;
      mappingRight?: string;
      mappingRightType?: string;
    }>;
    emptyText: string;
    /** For input: columns with manual filters */
    filteredColumnIds?: Set<string>;
    /** For output: columns marked as match keys */
    matchKeyColumnIds?: Set<string>;
  }) {
    const {
      tableName,
      isCrossDoc,
      columns,
      emptyText,
      filteredColumnIds,
      matchKeyColumnIds,
    } = config;

    return html`
      <div class=${styles.tableCard}>
        <div class=${styles.tableName}>
          <span class=${styles.tableIcon}>${DatabaseTableViewIcon()}</span>
          ${tableName}
          ${isCrossDoc
            ? html`<span class=${styles.crossDocIcon}
                >${LinkedPageIcon({ width: '10', height: '10' })}</span
              >`
            : nothing}
        </div>
        ${columns.length > 0
          ? html`
              <ul class=${styles.columnList}>
                ${columns.slice(0, 5).map(col => {
                  const isFiltered = filteredColumnIds?.has(col.id) ?? false;
                  const isMatchKey = matchKeyColumnIds?.has(col.id) ?? false;
                  const hasMapping = col.mappingLeft && col.mappingRight;
                  return html`
                    <li class=${styles.columnItem}>
                      ${hasMapping
                        ? html`
                            <span
                              class=${styles.columnName}
                              title=${col.mappingLeftTooltip ??
                              col.mappingLeft ??
                              ''}
                              >${col.mappingLeft}</span
                            >
                            ${col.mappingLeftType
                              ? html`<span
                                  class=${styles.typeChip}
                                  style=${styleMap({
                                    backgroundColor: getTypeColor(
                                      col.mappingLeftType
                                    ),
                                  })}
                                  >${normalizeTypeLabel(
                                    col.mappingLeftType
                                  )}</span
                                >`
                              : html`<span></span>`}
                            <span class=${styles.mappingArrow}>→</span>
                            <span class=${styles.columnName}
                              >${col.mappingRight}</span
                            >
                            ${col.mappingRightType
                              ? html`<span
                                  class=${styles.typeChip}
                                  style=${styleMap({
                                    backgroundColor: getTypeColor(
                                      col.mappingRightType
                                    ),
                                  })}
                                  >${normalizeTypeLabel(
                                    col.mappingRightType
                                  )}</span
                                >`
                              : html`<span></span>`}
                          `
                        : html`<span class=${styles.columnNameFull}
                              >${col.name}</span
                            >
                            <span
                              class=${styles.typeChip}
                              style=${styleMap({
                                backgroundColor: getTypeColor(col.type),
                              })}
                              >${normalizeTypeLabel(col.type)}</span
                            >`}
                      <span class=${styles.iconSlot}>
                        ${isMatchKey
                          ? LinkIcon({ width: '12', height: '12' })
                          : isFiltered
                            ? FilterIcon({ width: '12', height: '12' })
                            : nothing}
                      </span>
                    </li>
                  `;
                })}
                ${columns.length > 5
                  ? html`<li class=${styles.moreHint}>
                      +${columns.length - 5} more
                    </li>`
                  : nothing}
              </ul>
            `
          : html`<div class=${styles.emptyHint}>${emptyText}</div>`}
      </div>
    `;
  }

  private _renderInputSection(
    sourceTableId: string | null | undefined,
    resolvedName: string | null,
    isCrossDoc: boolean,
    isWebhookTrigger: boolean
  ) {
    // No source table configured → show "Not configured" like Output
    if (!sourceTableId) {
      return html`<div class=${styles.emptyState}>
        ${I18n.t('com.streamlain.streamlain.process.not-configured')}
      </div>`;
    }

    const bridge = window.__databaseBridge;
    const inputMappings = this.model.props.inputMappings ?? [];
    const selectedColumnIds = this.model.props.selectedColumns ?? [];
    const inputTables = this.model.props.inputTables ?? [];
    const currentDocId = this.model.store.id;

    // Build the list of all tables to render: lead first, then secondaries
    const tablesToRender: Array<{
      tableId: string;
      inputTableId?: string; // InputTable.id (nanoid) for mapping lookup
      name: string;
      isCrossDoc: boolean;
      role: 'lead' | 'secondary';
      filterGroup?: FilterGroup;
    }> = [];

    // Lead table
    const leadEntry = inputTables.find(t => t.role === 'lead');
    tablesToRender.push({
      tableId: sourceTableId,
      inputTableId: leadEntry?.id,
      name: isWebhookTrigger
        ? I18n.t('com.streamlain.streamlain.process.canvas.webhook-input')
        : resolvedName || 'Source Table',
      isCrossDoc,
      role: 'lead',
      filterGroup: this.model.props.filterGroup,
    });

    // Secondary tables
    for (const t of inputTables.filter(t => t.role === 'secondary')) {
      const dbInfo = bridge?.findDatabaseByBlockId?.(t.tableId);
      tablesToRender.push({
        tableId: t.tableId,
        inputTableId: t.id,
        name: dbInfo?.databaseTitle ?? t.tableName,
        isCrossDoc: dbInfo ? dbInfo.docId !== currentDocId : !!t.docId,
        role: 'secondary',
        filterGroup: t.filterGroup,
      });
    }

    // Render each table card with the same logic
    const cards = tablesToRender.map(table => {
      const allColumns =
        table.tableId && !isWebhookTrigger
          ? (bridge?.getColumnsForDatabase?.(table.tableId) ?? [])
          : [];

      // Resolve columns: inputMappings (schema mode) > selectedColumns (checkbox mode)
      let displayColumns: Array<{
        id: string;
        name: string;
        type: string;
        mappingLeft?: string;
        mappingRight?: string;
      }>;

      // Mappings for this specific table
      const tableMappings = inputMappings.filter(
        m =>
          m.columnId &&
          (m.inputTableId === table.inputTableId ||
            // Legacy: no inputTableId → belongs to lead
            (!m.inputTableId && table.role === 'lead'))
      );

      if (tableMappings.length > 0) {
        // Schema mode: show mapped columns with arrows
        displayColumns = tableMappings
          .map(
            (m: {
              schemaFieldId: string;
              schemaFieldName?: string;
              schemaFieldType?: string;
              columnId: string | null;
            }) => {
              const col = allColumns.find(c => c.id === m.columnId);
              return col
                ? {
                    id: col.id,
                    name: col.name,
                    type: col.type,
                    mappingLeft: col.name,
                    mappingLeftType: col.type,
                    mappingRight: m.schemaFieldName ?? m.schemaFieldId,
                    mappingRightType: m.schemaFieldType,
                  }
                : null;
            }
          )
          .filter(Boolean) as typeof displayColumns;
      } else {
        // Checkbox mode: selectedColumns uses composite keys "tableId:columnId"
        displayColumns = allColumns.filter(
          c =>
            selectedColumnIds.includes(c.id) ||
            selectedColumnIds.includes(`${table.tableId}:${c.id}`)
        );
      }

      // Extract filtered column IDs from filterGroup
      const filteredColumnIds = new Set<string>();
      const extractFilteredColumns = (
        group: typeof table.filterGroup | undefined
      ): void => {
        if (!group) return;
        if (group.type === 'group') {
          for (const cond of group.conditions) {
            if (cond.type === 'filter' && cond.left?.name) {
              filteredColumnIds.add(cond.left.name);
            } else if (cond.type === 'group') {
              extractFilteredColumns(cond);
            }
          }
        }
      };
      extractFilteredColumns(table.filterGroup);

      return this._renderTableSection({
        tableName: table.name,
        isCrossDoc: table.isCrossDoc,
        columns: displayColumns,
        emptyText: 'No columns',
        filteredColumnIds,
      });
    });

    return html`${cards}`;
  }

  private _renderOutputSection(
    outputTables: Array<{
      tableId: string;
      resolvedName: string;
      isCrossDoc: boolean;
      matchConfigId?: string;
    }>,
    outputMappings: Array<{
      tableId: string;
      targetColumnId: string;
      responseField?: string;
      responseFieldName?: string;
      responseFieldType?: string;
    }> | null
  ) {
    if (!outputTables.length) {
      return html`<div class=${styles.emptyState}>
        ${I18n.t('com.streamlain.streamlain.process.not-configured')}
      </div>`;
    }

    const bridge = window.__databaseBridge;
    const matchConfigs = this.model.props.matchConfigs ?? [];
    // sl-1ahs.6.1: lastResponseStructure lookup removed together with the
    // Test Run infrastructure. Canvas block now renders mapped columns
    // without response-field type hints — the sidebar's schema-based
    // mapping UI is the source of truth for type information.

    const cards = outputTables.map(table => {
      const targetColumns =
        bridge?.getColumnsForDatabase?.(table.tableId) ?? [];

      // Filter mappings to this specific table
      const tableMappings = (outputMappings ?? []).filter(m =>
        m.tableId ? m.tableId === table.tableId : true
      );

      // sl-g3on.4.1: Resolve responseField → { id, displayName } so the
      // renderer can prefer the workflow output field name over the raw ID.
      // `responseFieldName` is populated by the sidebar writer in
      // sl-g3on.4.2; legacy mappings without it fall back to the raw ID.
      const responseFieldMap = new Map<
        string,
        { id: string; displayName: string; type?: string }
      >(
        tableMappings
          .filter(m => m.responseField)
          .map(m => [
            m.targetColumnId,
            {
              id: m.responseField as string,
              displayName: m.responseFieldName ?? (m.responseField as string),
              type: m.responseFieldType,
            },
          ])
      );

      const mappedColumnIds = new Set(tableMappings.map(m => m.targetColumnId));

      // sl-mau5: Get match key column IDs from MatchConfig
      const rowMcId = table.matchConfigId;
      const rowMc = rowMcId
        ? matchConfigs.find(mc => mc.id === rowMcId)
        : undefined;
      const matchKeyColumnIds = new Set(
        (rowMc?.keys ?? []).map(k => k.columnId)
      );

      // Only show mapped columns: responseField → columnName
      const mappedColumns = targetColumns
        .filter(col => mappedColumnIds.has(col.id))
        .map(col => {
          const responseField = responseFieldMap.get(col.id);
          return {
            ...col,
            mappingLeft: responseField?.displayName,
            mappingLeftTooltip: responseField?.id,
            mappingLeftType: responseField?.type,
            mappingRight: col.name,
            mappingRightType: col.type,
          };
        });

      return this._renderTableSection({
        tableName: table.resolvedName || 'Target Table',
        isCrossDoc: table.isCrossDoc,
        columns: mappedColumns,
        emptyText: 'No mappings',
        matchKeyColumnIds,
      });
    });

    return html`${cards}`;
  }

  // ===========================================================================
  // Formatting helpers
  // ===========================================================================

  private _formatTrigger(
    config: { type?: string; cronPreset?: string; preset?: string } | null
  ): string {
    if (!config)
      return I18n.t('com.streamlain.streamlain.process.schedule.manual');
    const type = config.type ?? 'manual';

    if (type === 'manual')
      return I18n.t('com.streamlain.streamlain.process.schedule.manual');
    if (type === 'webhook')
      return I18n.t('com.streamlain.streamlain.process.schedule.webhook');

    if (type === 'scheduled') {
      const preset = config.cronPreset ?? config.preset ?? '';
      const KEY_PREFIX = 'com.streamlain.streamlain.process.schedule.';
      const presetKeyMap: Record<string, string> = {
        every_10_sec: 'every-10-sec',
        every_30_sec: 'every-30-sec',
        every_1_min: 'every-1-min',
        every_5_min: 'every-5-min',
        every_15_min: 'every-15-min',
        every_30_min: 'every-30-min',
        every_hour: 'every-hour',
        hourly: 'every-hour',
        daily_9am: 'daily-9am',
        daily_6pm: 'daily-6pm',
        weekly_monday: 'weekly-monday',
        weekly_monday_9am: 'weekly-monday',
        monthly_first_9am: 'monthly',
        custom: 'custom',
      };
      const key = presetKeyMap[preset];
      return key
        ? I18n.t(`${KEY_PREFIX}${key}`)
        : preset ||
            I18n.t('com.streamlain.streamlain.process.trigger-scheduled');
    }
    return type;
  }

  private _formatLastRun(timestamp: number | null): string {
    if (!timestamp) {
      return I18n.t('com.streamlain.streamlain.process.last-run.never');
    }
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) {
      return I18n.t('com.streamlain.streamlain.process.last-run.just-now');
    }
    if (minutes < 60) {
      return I18n.t('com.streamlain.streamlain.process.last-run.minutes-ago', {
        count: minutes,
      });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return I18n.t('com.streamlain.streamlain.process.last-run.hours-ago', {
        count: hours,
      });
    }
    const days = Math.floor(hours / 24);
    return I18n.t('com.streamlain.streamlain.process.last-run.days-ago', {
      count: days,
    });
  }

  // ===========================================================================
  // Validation & handlers
  // ===========================================================================

  /**
   * sl-optc: Validate automation config before enabling schedule.
   * Prevents infinite loops by requiring:
   * 1. Status tracking enabled (to skip already-processed rows)
   * 2. Match keys when input table == output table (to update, not append)
   * 3. "If not found: Fail" when input == output (no upsert/create allowed)
   *
   * Returns i18n keys for translation in React layer.
   */
  private _validateEnableSchedule(): { valid: boolean; reasonKey?: string } {
    const { statusTracking, sourceTableId, outputTables } = this.model.props;

    // Rule 1: Status tracking must be enabled AND fully configured
    if (!statusTracking?.enabled) {
      return {
        valid: false,
        reasonKey: 'status-tracking-required',
      };
    }

    // Rule 1b: If status_column mode, a column must be selected
    const mode = statusTracking.mode ?? 'status_column';
    if (mode === 'status_column' && !statusTracking.columnId) {
      return {
        valid: false,
        reasonKey: 'status-tracking-incomplete',
      };
    }

    // Rule 2 & 3: If input table == output table, special restrictions apply
    if (sourceTableId && outputTables?.length) {
      const inputEqualsOutput = outputTables.some(
        t => t.tableId === sourceTableId
      );

      if (inputEqualsOutput) {
        // sl-mau5: Check if the output table has a row MatchConfig
        const matchTable = outputTables.find(t => t.tableId === sourceTableId);
        const matchConfigs = this.model.props.matchConfigs ?? [];
        const rowMc = matchTable?.matchConfigId
          ? matchConfigs.find(mc => mc.id === matchTable.matchConfigId)
          : undefined;

        if (!rowMc || rowMc.keys.length === 0) {
          return {
            valid: false,
            reasonKey: 'match-key-required',
          };
        }

        // Check notFoundBehavior - must be 'fail', not 'create'
        if (
          rowMc.notFoundBehavior === 'create' ||
          rowMc.notFoundBehavior === undefined
        ) {
          return {
            valid: false,
            reasonKey: 'create-not-allowed',
          };
        }
      }
    }

    return { valid: true };
  }

  private readonly _handleToggleEnabled = (e: Event) => {
    e.stopPropagation();
    const currentConfig = this.model.props.triggerConfig || {};
    const currentEnabled =
      window.__automationRuntimeBridge?.getRuntime(this.model.id)?.schedule
        ?.enabled ??
      currentConfig.enabled ??
      false;
    const newEnabled = !currentEnabled;

    // sl-1ahs.4.1: Only scheduled triggers support enable/disable toggle
    if (newEnabled && currentConfig.type !== 'scheduled') {
      window.dispatchEvent(
        new CustomEvent('automation-validation-error', {
          detail: {
            blockId: this.model.id,
            reasonKey: 'toggle-only-scheduled',
          },
          bubbles: true,
        })
      );
      return;
    }

    // sl-optc: Validate before enabling
    if (newEnabled) {
      const validation = this._validateEnableSchedule();
      if (!validation.valid) {
        // Dispatch event for React layer to show notification with i18n key
        window.dispatchEvent(
          new CustomEvent('automation-validation-error', {
            detail: {
              blockId: this.model.id,
              reasonKey: validation.reasonKey,
            },
            bubbles: true,
          })
        );
        return; // Don't enable
      }
    }

    const newTriggerConfig = {
      ...currentConfig,
      type: currentConfig.type || 'scheduled',
      cronPreset: currentConfig.cronPreset || 'every_5_min',
      enabled: newEnabled,
    };

    this.model.props.triggerConfig = newTriggerConfig;

    window.dispatchEvent(
      new CustomEvent('automation-trigger-sync', {
        detail: {
          blockId: this.model.id,
          docId: this.model.store.id,
          triggerConfig: newTriggerConfig,
        },
        bubbles: true,
      })
    );
  };

  private readonly _handleToggleDetails = (e: Event) => {
    e.stopPropagation();
    this._detailsExpanded = !this._detailsExpanded;
  };

  /**
   * Re-run after a failed run. Dispatches the same `automation-run` event
   * the global execution listener (use-execution-listener.ts) handles for
   * the sidebar Run button.
   */
  private readonly _handleRetryRun = (e: Event) => {
    e.stopPropagation();
    const docId = this.model.store.id;
    if (!docId) return;

    window.dispatchEvent(
      new CustomEvent('automation-run', {
        detail: {
          blockId: this.model.id,
          docId,
          mode: 'execute',
        },
        bubbles: true,
      })
    );
  };

  private readonly _handleOpenConfig = (e: Event) => {
    e.stopPropagation();

    window.dispatchEvent(
      new CustomEvent('automation-configure', {
        detail: { blockId: this.model.id },
        bubbles: true,
      })
    );

    const sidebar = this.std.getOptional(SidebarExtensionIdentifier);
    if (sidebar) {
      sidebar.open('streamlain-automation');
    }
  };
}

// =============================================================================
// Interaction Extension
// =============================================================================

export const AutomationBlockInteraction =
  GfxViewInteractionExtension<AutomationCanvasBlockComponent>(
    AutomationBlockSchema.model.flavour,
    {
      selectionBorderRadius: 8,
      resizeConstraint: {
        allowedHandlers: [],
      },

      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),

      handleSelection: ({ gfx, view, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const { editing } = gfx.selection;
          const alreadySelected = gfx.selection.has(model.id);

          if (!multiSelect && selected && (alreadySelected || editing)) {
            if (model.isLocked()) return;

            if (alreadySelected && editing) {
              return;
            }

            gfx.selection.set({
              elements: [model.id],
              editing: true,
            });

            view.updateComplete
              .then(() => {
                if (!view.isConnected) return;
              })
              .catch(console.error);
          } else {
            context.default(context);
          }
        },
      }),
    }
  );
